import { GridToolbarContainer, useGridApiContext, gridFilteredSortedRowIdsSelector, gridColumnDefinitionsSelector } from "@mui/x-data-grid";
import { Button, Typography, Box, FormControl, Select, TextField, IconButton } from "@mui/material";
import { useState, useEffect } from "react";

// icons
import { Download as ExportBtnIcon, FilterList as FiltersBtnIcon, FilterListOff as FiltersOffIcon, Close as ClearFilterIcon } from "@mui/icons-material";

export default function CustomToolbar({
    totalCount = 0,
    filter = {},
    exportedFilename = "Data_Export",
    onExportClick = null,
    otherBtns = null
}) {
    const apiRef = useGridApiContext();
    const {
        rowsCount = 0,
        columns = [],
        active = { column: '', value: '' },
        onChange
    } = filter;
    const [showFilters, setShowFilters] = useState(false);
    const filterableColumns = columns.filter(col => col.filterable !== false && col.headerName);

    //default to first filterable column
    useEffect(() => {
        if (filterableColumns.length > 0 && !active.column && onChange) {
            onChange({ ...active, column: filterableColumns[0].field });
        }
    }, [filterableColumns, onChange]);

    //update filter value
    const updateFilter = (field, val) => {
        if (onChange) {
            onChange({ ...active, [field]: val });
        }
    };

    //clear filters
    const handleClearFilters = () => {
        if (onChange) {
            onChange({ ...active, value: '' });
        }
    };

    //export CSV
    const handleExportCSV = () => {
        const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);

        //filter out columns that are explicitly marked as not exportable
        const exportableColumns = gridColumnDefinitionsSelector(apiRef).filter(
            (col) =>
                col.field !== "__check__" && // Hide checkbox column
                !!col.headerName &&           // Hide columns without headers
                col.export !== false     // Hide columns where exportable is false
        );
        const headers = exportableColumns.map((col) => `"${col.headerName}"`).join(",");
        const csvRows = filteredSortedRowIds.map((id) => {
            return exportableColumns
                .map((col) => {
                    const cellValue = apiRef.current.getCellValue(id, col.field) ?? "";
                    const stringValue = typeof cellValue === 'object'
                        ? JSON.stringify(cellValue)
                        : String(cellValue);

                    return `"${stringValue.replace(/"/g, '""')}"`;
                })
                .join(",");
        });
        const csvContent = [headers, ...csvRows].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        const dateSuffix = new Date().toISOString().split('T')[0];
        link.setAttribute("href", url);
        link.setAttribute("download", `${exportedFilename}_${dateSuffix}.csv`);
        link.click();
    };
    return (
        <GridToolbarContainer sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: 180 }} aria-live="polite" aria-atomic="true">
                Displaying <strong>{rowsCount}</strong> of {totalCount} records
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>

                {/* Export CSV button */}
                <Button size="small" startIcon={<ExportBtnIcon />} onClick={onExportClick || handleExportCSV}>
                    Export
                </Button>

                {/* Other buttons */}
                {otherBtns}

                {/* Filters button */}
                {filterableColumns.length > 0 && (
                    <Button
                        size="small"
                        startIcon={showFilters ? <FiltersOffIcon /> : <FiltersBtnIcon />}
                        onClick={() => setShowFilters(!showFilters)}
                        aria-controls="filter-panel"
                        aria-expanded={showFilters}
                    >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                    </Button>)}
                {showFilters && (
                    <Box id="filter-panel" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        <FormControl variant="standard" sx={{ minWidth: 140, mr: 2 }}>
                            <Select
                                native
                                autoFocus
                                value={active.column}
                                onChange={(e) => updateFilter('column', e.target.value)}
                                inputProps={{
                                    name: 'column',
                                    id: 'filter-column-select',
                                    'aria-label': 'Select Column to Filter'
                                }}
                                sx={{ fontSize: '0.85rem', height: '32px' }}
                            >
                                {filterableColumns.map((col) => (
                                    <option key={col.field} value={col.field} style={{ fontSize: '0.85rem' }}>
                                        {col.headerName}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            variant="standard"
                            placeholder="Input value to filter"
                            size="small"
                            value={active.value}
                            onChange={(e) => updateFilter('value', e.target.value)}
                            inputProps={{
                                'aria-label': `Filter value for ${active.column}`,
                                'type': 'search'
                            }}
                            sx={{
                                width: 160,
                                '& .MuiInputBase-input': { fontSize: '0.85rem', py: '6px' },
                                '& input::-webkit-search-decoration, & input::-webkit-search-cancel-button, & input::-webkit-search-results-button, & input::-webkit-search-results-decoration': {
                                    display: 'none',
                                },
                            }}
                        />

                        {/* Clear Button */}
                        {active.value && (
                            <IconButton
                                size="small"
                                onClick={handleClearFilters}
                                sx={{ ml: 0.5 }}
                                aria-label="Clear current filter"
                                title="Clear filter"
                                color="primary"
                            >
                                <ClearFilterIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>
        </GridToolbarContainer>
    );
}